import React, { ButtonHTMLAttributes } from 'react';
import { Link } from 'react-router-dom';
import { IconElements } from '../../config/icons';

/** Neutral meaning white when white theme and black when black theme */
export type ButtonTints =
    | 'neutral'
    | 'neutral-no-hover'
    | 'transparent'
    | 'green'
    | 'p-green'
    | 'yellow'
    | 'pink'
    | 'grey'
    | 'blue'
    | 'sober';

export type ButtonSize = 'sm' | 'md';

export type WobbleArrowType = 'left' | 'right' | undefined;

const getWobbleArrowIcon = (wobbleArrowType: WobbleArrowType, tint: ButtonTints) => {
    const textCol = getTextColorClass(tint);
    const classNameArrow = "ml-2 group-hover:animate-bouncex w-4 h-4";

    return wobbleArrowType === 'left'
        ? <IconElements.ArrowLeft className={`${textCol} ${classNameArrow}`} />
        : wobbleArrowType === 'right'
            ? <IconElements.ArrowRight className={`${textCol} ${classNameArrow}`} />
            : undefined
}

const getBackgroundColorClass = (tint: ButtonTints) => {
    switch (tint) {
        case 'transparent':
            return 'bg-transparent hover:bg-bnd-green-hover hover:text-white transition dark:bg-bnd-black-2';
        case 'neutral':
            return 'bg-bnd-white-1 hover:bg-bnd-green-hover hover:text-white transition dark:bg-bnd-black-2';
        case 'neutral-no-hover':
            return 'bg-bnd-white-1 transition dark:bg-bnd-black-2 border border-bnd-green';
        case 'green':
            return 'bg-bnd-green hover:bg-bnd-green-hover transition shadow-lg dark:shadow-0';
        case 'p-green':
            return 'bg-bnd-green hover:bg-bnd-green-hover transition text-white';
        case 'yellow':
            return 'bg-bnd-yellow hover:bg-yellow-300 transition shadow-lg dark:shadow-0';
        case 'blue':
            return 'bg-bnd-blue hover:bg-yellow-300 transition shadow-lg dark:shadow-0';
        case 'grey':
            return 'bg-bnd-grey-1 hover:bg-bnd-grey-1 transition shadow-lg dark:shadow-0';
        case 'pink':
            return 'bg-bnd-pink hover:bg-bnd-pink-hover transition shadow-lg dark:shadow-0';
        case 'sober':
            return 'bg=transparent hover:bg-transparent transition';
    }
};

const getTextColorClass = (tint: ButtonTints) => {
    switch (tint) {
        case 'transparent':
            return 'text-bnd-green dark:bg-bnd-white-1';
        case 'neutral':
            return 'text-bnd-green dark:bg-bnd-white-1';
        case 'neutral-no-hover':
            return 'text-bnd-green dark:bg-bnd-white-1';
        case 'green':
            return 'text-bnd-white-1 dark:text-bnd-dark-1';
        case 'p-green':
            return 'text-bnd-white-1';
        case 'yellow':
            return 'bg-bnd-yellow';
        case 'blue':
            return 'text-bnd-white-1 dark:text-bnd-dark-1';
        case 'grey':
            return 'text-bnd-grey-2 dark:text-bnd-dark-1';
        case 'pink':
            return 'text-bnd-white-1 dark:text-bnd-dark-1';
        case 'sober':
            return 'text-gray-800 underline';
    }
};

const getSizeClass = (size: ButtonSize) => {
    switch (size) {
        case 'sm':
            return 'leading-6';
        case 'md':
            return 'leading-9';
    }
};

const getBorderClass = (tint: ButtonTints) => {
    switch (tint) {
        case 'neutral':
            return 'border-2 border-bnd-green hover:border-bnd-green-hover transition';
        default:
            return 'border-2 border-transparent hover:border-transparent';
    }
};

export const Button: React.FC<
    {
        isLoading?: boolean;
        linkTo?: string;
        tint?: ButtonTints;
        size?: ButtonSize;
        isDisabled?: boolean;
        showWobbleArrow?: boolean;
        wobbleArrowType?: WobbleArrowType;
    } & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'size'>
> = ({ linkTo, className, children, isDisabled = false, size = 'md', tint = 'neutral', showWobbleArrow = false, wobbleArrowType = 'right', id, ...rest }) => {
    if (linkTo) {
        return (
            <Link
                to={linkTo}
                id={id}
                className={`relative px-4 py-1.5 text-center truncate ${getSizeClass(
                    size,
                )} rounded-full shadow-lg dark:shadow-0 ${getBorderClass(tint)} ${getTextColorClass(
                    tint,
                )} ${getBackgroundColorClass(tint)} ${
                    !isDisabled ? 'cursor-pointer' : 'cursor-not-allowed'
                } ${className}`}
            >
                {children}
            </Link>
        );
    }

    return (
        <button
            className={`group block relative px-4 py-1.5 text-center truncate focus:outline-none ${getSizeClass(
                size,
            )} rounded-full ${getBorderClass(tint)} ${getTextColorClass(tint)} ${getBackgroundColorClass(
                tint,
            )} ${className} ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
            id={id}
            disabled={isDisabled}
            {...rest}
        >
            <div className={`flex flex-row gap-x-2 justify-center items-center`}>
                <div style={{display: showWobbleArrow ? 'block' : 'none'}}>
                    {getWobbleArrowIcon(wobbleArrowType, tint)}
                </div>
                {children}
            </div>
        </button>
    );
};
